import { useState, type ReactNode } from 'react';
import { useFlashFlag } from '@/hooks/useFlashFlag';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, AlertTriangle, AlertCircle, Clock, Check, Copy, FileEdit, Download, RefreshCw, Pencil } from 'lucide-react';
import {
  PreflightCopy,
  ReportStatus,
  ReportSummaryKind,
  FilingOrigin,
  AmendmentModel,
  FilingGranularity,
  assertNever,
  electionDesignationLabel,
  isOnReportInclusion,
  pendingAtFilingWarningBody,
  pendingAtFilingManualPortalBody,
  manualPortalEntryHeading,
  manualPortalEntryBody,
} from '@signalvote/shared';
import type {
  SoftGateWarning,
  ContributionReportItem,
  ExpenditureReportItem,
  RefundReportItem,
  ReportRowInclusion,
  ComplianceReportDetailDto,
  LiveExportDraftDto,
  FederalSummaryDegradedDetailDto,
} from '@signalvote/shared';
import { cn } from '@/lib/utils';
import { useRefresh } from '@signalvote/data';
import { Button } from '@/components/ui/button';
import { PendingButton } from '@/components/ui/pending-button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
  useFinanceReportDetail,
  useFilingChain,
  useDownloadReportPackage,
  useDownloadReportCover,
  useFilingPreview,
  useRefreshFilingPreview,
  useMarkExportBatchUploaded,
  useFilingPreflight,
} from '@/hooks/queries/useFinanceReports';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { usePortalUploadModel, filingDestinationPhrase } from '@/hooks/finance/usePortalUploadModel';
import { useMarkContributionReported } from '@/hooks/queries/useFinanceContributions';
import { AuditLogPanel } from '../audit';
import { ContactDetail } from '../contacts/ContactDetail';
import { formatAmount, formatDate } from '../utils';
import { statusBadge, lockBadge, pendingAtFilingCount, isPostFiling, chainStanding } from './reportUtils';
import { FilingLockCopy, amendmentInProgressNotice, externalLabelTooltip, supersededNotice } from './filingLockCopy';
import {
  openAlreadyFiledNudge,
  portalNameFor,
} from './amendmentWorkflowHint';
import { ReopenPeriodAffordance } from './ReopenPeriodAffordance';
import { ReopenReportAffordance } from './ReopenReportAffordance';
import { FilePackageButton } from './FilePackageButton';
import { AuthorizeSendButton } from './AuthorizeSendButton';
import { ExportDeltaButton } from './ExportDeltaButton';
import { GenerateWholeReportButton } from './GenerateWholeReportButton';
import { ExportUploadGateBanner } from './ExportUploadGateBanner';
import { MarkFiledInPortalButton } from './MarkFiledInPortalButton';
import { ReceiptEditor } from './ReceiptEditor';
import { ReceiptChaseOpenItem } from './ReceiptChaseOpenItem';
import { SentReportEditWarning } from './SentReportEditWarning';
import { receivingOfficePhrase, withReceivingOffice } from './receivingOffice';
import { BeginningBalanceEditor } from './BeginningBalanceEditor';
import { FinalReportEditor } from './FinalReportEditor';
import { MississippiCoverReviewButton } from './MississippiCoverReviewButton';
import { CashRecalcRippleNotice } from './CashRecalcRippleNotice';
import { PostFileSuccessPanel } from './PostFileSuccessPanel';
import { PortalFollowUpsChecklist } from './PortalFollowUpsChecklist';
import { PortalRunPanel } from './PortalRunPanel';
import { HardFailureList } from './HardFailureList';
import { PortalActionList } from './PortalActionList';
import { PreflightUnready } from './PreflightUnready';
import { triggerBrowserDownload } from './downloadHelpers';
import { DownloadAuditPacketButton } from './DownloadAuditPacketButton';
import { Form3SummaryView } from './Form3SummaryView';
import { VaCometSummaryView } from './VaCometSummaryView';
import { AlabamaSummaryView } from './AlabamaSummaryView';
import { NorthCarolinaSummaryView } from './NorthCarolinaSummaryView';
import { ReportedRowBadge } from './ReportedRowBadge';
import { AmendmentDiffPanel } from './AmendmentDiffPanel';
import { FilingHistory } from './FilingHistory';
import { SigningEvidencePanel } from './SigningEvidencePanel';
import { readyProfile, profileUnreadyMessage } from '@/lib/complianceProfileRead';
import { answered } from '@/lib/reportsRead';

// ============================================================================
// Props
// ============================================================================

interface ReportDetailPageProps {
  reportId: string;
  /** The row whose 72/24-hour notice the portal-run panel runs instead of the whole report. */
  noticeOf?: string | null;
}

// ============================================================================
// Component
// ============================================================================

export function ReportDetailPage({ reportId, noticeOf = null }: ReportDetailPageProps) {
  const navigate = useNavigate();
  const [showAuditPanel, setShowAuditPanel] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCover, setDownloadingCover] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // BSW-MissingInfo: clicking the row-level warning opens a Sheet with the
  // contact preloaded so the user can fill employer/occupation without
  // leaving the report.
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const refresh = useRefresh();
  const reportRead = useFinanceReportDetail(reportId);
  const report = reportRead.status === 'ready' ? reportRead.data : undefined;
  // The same chain FilingHistory reads (same key, one request).
  const chain = answered(useFilingChain(
    report && (isPostFiling(report.status) || report.isAmendment) ? report.id : null,
  ));
  // #571 — the served compliance profile is now the source for BOTH the serving-model axes
  // (uploadIsFiling/certifyAtGenerate) AND the amendment axis (amendmentModel). Its read status drives
  // the pre-filing submission-surface settled-guard (see `servingModelSettled`).
  const profileRead = useComplianceProfile();
  const profile = readyProfile(profileRead);
  const profileUnready = profileUnreadyMessage(profileRead);
  // #313/#481 — the filing-TRANSPORT axis (NC files by email, not a portal upload). Resolved once in
  // usePortalUploadModel from the SERVED `profile.filingTransport` (#478) so the pre-generate strings
  // never re-hardcode `stateCode === 'NC'`. `filingTransport` is the full axis passed to
  // PostFileSuccessPanel; `isEmailFiling` is its `=== 'email'` projection used inline here.
  const portalModel = usePortalUploadModel();
  // Confirm Filed unmounts its button once the report reads Filed; the confirmation lives here.
  const [recordedFiledNote, setRecordedFiledNote] = useState<string | null>(null);
  const { isEmailFiling, filingTransport } = portalModel;
  const { downloadPackage } = useDownloadReportPackage();
  const { downloadCover } = useDownloadReportCover();
  // FPD-020 — Pull the unreconciled-block from the preview endpoint so the
  // report-detail page can render the "Awaiting settlement" badge next to
  // in-period Pending rows + dual-count header. Only meaningful for Ready
  // reports; Filed/Amended reports show a static snapshot already.
  const preview = answered(useFilingPreview(
    report?.status === ReportStatus.Ready ? reportId : null,
  ));
  const refreshFilingPreview = useRefreshFilingPreview();

  // A contact save names FinanceContact in its write set, which refreshes the report's
  // missing-info icons and warning count.
  const closeContactEditor = () => setEditingContactId(null);

  // "Pick up changes" — the report detail endpoint recomputes line items,
  // totals, schedules, and warnings LIVE on every fetch (it is NOT a stored
  // snapshot for pre-filing reports). So after the treasurer edits a
  // transaction in the ledger, the only thing that can lag is this page's
  // query cache. Re-fetching the detail (and the Ready-only filing preview)
  // surfaces the edit — no backend regenerate, never touching the filing path.
  const handleRefresh = () => {
    void refresh(useFinanceReportDetail, reportId);
    void refreshFilingPreview(reportId);
  };

  // Pencil click-through to the editing surface. The Close ledger is the one
  // place a treasurer can change a transaction's content (amount/date/payee/
  // category/purpose) or its status (confirm a Pending row, restore a voided
  // one). We thread the return-to-report payload so the ledger renders its
  // "Back to report" affordance, plus the row's id so a future per-row focus
  // can scroll to it (the ledger already carries the param contract).
  const navigateToEditContribution = (contributionId: string) => {
    void navigate({
      to: '/admin/finance/close',
      search: { returnTo: 'report', reportId, contributionId },
    });
  };
  const navigateToEditExpenditure = (expenditureId: string) => {
    void navigate({
      to: '/admin/finance/close',
      search: { returnTo: 'report', reportId, expenditureId },
    });
  };
  // Issue #117 — deep-link the degradation banner at the offending record. Routes to the same
  // Close-ledger row an inline row-edit would, so the treasurer lands on exactly what broke.
  const navigateToDegradedRecord = (detail: FederalSummaryDegradedDetailDto) => {
    switch (detail.entityType) {
      case 'Contribution':
        navigateToEditContribution(detail.entityId);
        return;
      case 'Expenditure':
        navigateToEditExpenditure(detail.entityId);
        return;
      default:
        assertNever(detail.entityType);
    }
  };

  const handleDownload = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      const result = await downloadPackage(reportId);
      triggerBrowserDownload(result.zipBlob, result.fileName);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Failed to download package');
    } finally {
      setDownloading(false);
    }
  };

  // ADR-054 — the signable CRO-1000 cover, downloaded distinctly from the package zip (NC only).
  const handleDownloadCover = async () => {
    setDownloadError(null);
    setDownloadingCover(true);
    try {
      const result = await downloadCover(reportId);
      triggerBrowserDownload(result.blob, result.fileName);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Failed to download cover');
    } finally {
      setDownloadingCover(false);
    }
  };

  if (reportRead.status === 'failed' || reportRead.status === 'offline') {
    return (
      <div className="p-6">
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
          <span>
            {reportRead.status === 'offline'
              ? "You're offline, so we couldn't load this report."
              : "We couldn't load this report."}
          </span>
          <Button type="button" size="sm" variant="outline" onClick={() => void refresh(useFinanceReportDetail, reportId)}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (reportRead.status !== 'ready') {
    return <div className="p-6 text-center text-muted-foreground">Loading...</div>;
  }

  if (!report) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Report not found.</p>
        <button
          className="mt-2 text-sm text-primary underline"
          onClick={() => navigate({ to: '/admin/finance/reports' })}
        >
          Back to reports
        </button>
      </div>
    );
  }

  const badge = statusBadge(report);
  // ADR-019 / ADR-018 — computed lock chip + externally-filed provenance (read off the
  // DTO; never re-derive the calendar predicate). An ExternalPortal report has no
  // in-system package, so its package-only surfaces are suppressed and the AL SOS label
  // replaces the in-system "Filed" chrome.
  const lock = lockBadge(report);
  // A superseded report, or an original whose amendment is open, is not where this period's
  // corrections happen: it points at the report that is, instead of offering its own lock controls.
  const standing = chainStanding(report, chain?.chain);
  const inForce = standing.kind === 'inForce';
  const isExternallyFiled = report.filingOrigin === FilingOrigin.ExternalPortal;
  // An OPEN (still-editable) in-system period that already has a generated/filed report.
  // Editing is intentionally still free before the deadline; the soft nudge
  // warns the treasurer that those edits won't reach the state until a re-file/amend. Not
  // shown once locked (the reopen affordance carries the amendment messaging there) or for
  // an externally-filed report (no in-system re-file path).
  const showOpenAlreadyFiledNudge =
    inForce && !report.locked && !isExternallyFiled && report.status === ReportStatus.Filed;
  // #571/§46/§49 — copy inputs derived from the jurisdiction's SERVED amendment model
  // (`profile.amendmentModel`, ADR-021), no longer re-derived from the report's FormatKind. A
  // PortalOnly (append-only, AL) jurisdiction gets the export→upload→mark-filed explainer and the
  // append-only amendment nudge; federal (ElectronicResubmit) and LA/VA (SelfContainedUpload) keep the
  // plain in-system / whole-report framing. Portal name + state name never hard-code AL.
  const amendmentModel = profile?.amendmentModel ?? null;
  const isPortalOnlyWorkflow = amendmentModel === AmendmentModel.PortalOnly;
  // The export → upload → mark-filed stepper exports the not-yet-uploaded rows, so it needs BOTH the
  // append-only amendment model AND Incremental granularity (AL). NJ is PortalOnly but WholeReport —
  // its export-delta is refused — so it takes the whole-report Generate surface below; MD is
  // Incremental but not PortalOnly and keeps the whole-report surface too.
  const isIncrementalPortalWorkflow =
    isPortalOnlyWorkflow && profile?.granularity === FilingGranularity.Incremental;
  // #571/#545/#132 — serving-model-settled guard. `isPortalOnlyWorkflow` AND the whole-report vs
  // in-system split now BOTH come from the served profile, so the two former flashes (#545 AL formats
  // flash + #132 VA serving-model flash) merge into one profile-settled gate. Until the profile
  // resolves, `amendmentModel` is null → the page would briefly flash the non-portal-only default
  // before snapping to the AL PortalOnly stepper; gate the pre-filing submission surface on the profile
  // being ready. A failed or offline read names itself in place of the surface rather than guessing the
  // default one, which would offer the wrong filing steps for a PortalOnly state. `useComplianceProfile`
  // is campaign-scoped (not per-report), but a null-stateCode
  // report has no jurisdiction to wait on, so treat it as "nothing to wait for" — otherwise it would
  // gate on a profile that may legitimately be null (rules-only) and never render a submission surface.
  const servingModelSettled =
    report.stateCode == null || profileRead.status === 'ready';
  // A RemindOnly report (the AL annual) is filed directly in the portal — the app
  // builds no file for it, and `export-delta` / `file` return 400 `RemindOnlyReport`.
  // Hide Export/File entirely and render a reminder + attestation instead (the
  // "Mark filed in portal" finalization path stays). Portal-native, so the
  // celebratory success panel + pending-at-filing export remedy are suppressed too.
  const isRemindOnly = report.preparationMode === 'RemindOnly';
  // The DECLARED filing-receipt axis (`profile.filingReceipt`), never a `stateCode !== 'AL'` check —
  // a new jurisdiction must choose, so Mississippi can't silently inherit (or lose) the editor. While
  // the receipt chase is open its own open item owns the receipt capture, so the card's copy steps aside.
  const filingReceipt = profile?.filingReceipt ?? null;
  // proofOfFiling (Mississippi): the office's receipt is the only proof of filing — the confirm dialog
  // captures it, the receipt chase follows it up, and the edit-after-send warning guards the sent file.
  const receiptIsProofOfFiling = filingReceipt === 'proofOfFiling';
  const recordsFilingReceipt =
    (filingReceipt === 'reference' || receiptIsProofOfFiling) && !report.receiptChaseOpen;
  // #481 — prefer the SERVED portal name (`profile.portalName`, #478) for the active campaign; the FE
  // `portalNameFor(report.stateCode)` switch is only a fallback for the profile-loading window and
  // transports with no named destination (federal inSystem / rules-only both serve null), where the
  // name is neutralised in copy anyway. One line converges every `portalName={portalName}` consumer
  // on this page off the served value.
  const portalName = profile?.portalName ?? portalNameFor(report.stateCode);
  const submissionStateName = profile?.stateDisplayName ?? report.stateCode ?? 'your state';
  const isPreFiling = report.status === ReportStatus.Ready;
  // #132 — the serving-model axis (compliance profile's `uploadIsFiling`) tells a
  // whole-report portal upload (VA: generate ≠ file — the treasurer uploads to COMET, then
  // marks it filed) apart from federal in-system e-file (generate = file). The amendment
  // axis alone doesn't draw THIS boundary: VA's `amendmentModel` is `SelfContainedUpload`
  // while federal's is `ElectronicResubmit` — distinct, but both are whole-report re-files, so
  // amendmentModel keys the append-only-vs-whole-report split, not the upload-vs-e-file one.
  // VA's branch is "not the AL append-only PortalOnly flow AND upload ≠ file". A null
  // `uploadIsFiling` (profile loading / rules-only ruleset) falls through to the federal
  // File layout, matching the existing formats-loading behavior for AL.
  const isWholeReportPortalUpload =
    !isIncrementalPortalWorkflow && profile?.uploadIsFiling === false;
  // Issue #482 — every jurisdiction is now `uploadIsFiling = false`, so the whole-report flow above
  // covers BOTH Virginia (unsigned COMET payload — plain non-attesting Generate, certify in the
  // portal) AND federal (the `.fec` cover embeds the treasurer's signed certification, so its
  // generate step is the attestation File flow — `POST /file` — which produces the attested `.fec`
  // WITHOUT filing it; the treasurer uploads it to efo.fec.gov and marks it filed). This axis picks
  // which generate control the whole-report flow renders. Null (loading / rules-only) → false.
  const certifyAtGenerate = profile?.certifyAtGenerate === true;
  // #860 — where the treasurer files the generated file, from the ONE shared helper (the inline
  // duplicate of `the {state} {destination}` is collapsed here so there is one place to fix). It
  // names a portal-upload or email destination and neutralises for federal / a loading profile.
  const filingDestination = filingDestinationPhrase(portalModel);
  // ADR-051 — ONE server-resolved discriminator picks the summary view, replacing the
  // per-state boolean chain (`federalSummary != null` / `vaSchedules != null` /
  // `stateCode === 'AL'`) that used to be re-derived here. `renderSummaryView` below is the
  // single dispatch; a jurisdiction with no bespoke view resolves to `Flat` (the neutral flat
  // tables). `federalSummaryDegraded` (a build-failure null) still lights the banner above,
  // and correctly routes to `Flat` since a degraded summary is a null one.
  const summaryFirstLayout = report.summaryKind !== ReportSummaryKind.Flat;
  // Federal & Alabama fold ending cash into their cash bridge, so the standalone Ending
  // Balance tile is suppressed for them; VA and the flat view keep it.
  const hideEndingBalanceTile =
    report.summaryKind === ReportSummaryKind.Federal ||
    report.summaryKind === ReportSummaryKind.Alabama;
  const scheduleACount = report.scheduleASummary.reduce((sum, s) => sum + s.count, 0);
  const scheduleATotal = report.scheduleASummary.reduce((sum, s) => sum + s.total, 0);
  const scheduleBCount = report.scheduleBSummary.reduce((sum, s) => sum + s.count, 0);
  const scheduleBTotal = report.scheduleBSummary.reduce((sum, s) => sum + s.total, 0);

  // FLW-002 — server-computed splits (the frontend never recomputes itemizability
  // or inclusion; it reads the DTO flags). The main schedule shows the Included
  // (filed) set; excluded rows are demarcated below, not hidden.
  // "on the report" is Included PLUS already-reported rows folded back into an
  // open report's display; only the genuinely off-filing rows are demarcated below.
  const includedContributions = report.contributionItems.filter((i) => isOnReportInclusion(i.inclusion));
  const excludedContributions = report.contributionItems.filter((i) => !isOnReportInclusion(i.inclusion));
  // Split the on-report contributions on the server-computed itemization
  // verdict — the receipt twin of the expenditure split below. Itemized donors
  // carry identity on the filing; non-itemized donors file as a combined total but
  // still get a real inspectable row list (a treasurer may want to verify/edit them).
  const itemizedContributions = includedContributions.filter((i) => i.isItemized);
  const nonItemizedContributions = includedContributions.filter((i) => !i.isItemized);
  const nonItemizedContributionCount = nonItemizedContributions.length;
  const nonItemizedContributionTotal = nonItemizedContributions.reduce((sum, i) => sum + i.amount, 0);
  const itemizedContributionTotal = itemizedContributions.reduce((sum, i) => sum + i.amount, 0);
  const itemizedExpenditures = report.expenditureItems.filter(
    (i) => isOnReportInclusion(i.inclusion) && i.isItemized,
  );
  const nonItemizedExpenditures = report.expenditureItems.filter(
    (i) => isOnReportInclusion(i.inclusion) && !i.isItemized,
  );
  const nonItemizedCount = nonItemizedExpenditures.length;
  const nonItemizedTotal = nonItemizedExpenditures.reduce((sum, i) => sum + i.amount, 0);
  const itemizedExpenditureTotal = itemizedExpenditures.reduce((sum, i) => sum + i.amount, 0);
  // Pre-filing reports (Ready) are still editable: their rows recompute
  // live on every fetch. A Filed/Amended report is a frozen as-filed snapshot —
  // never offer pencil click-through-to-edit or a regenerate/refresh there. Same
  // gate as the BeginningBalanceEditor's `editable` prop.
  const preFilingEditable = report.status === ReportStatus.Ready;
  // A filed report's cover is a replay of the file it submitted (the server reads its loans and
  // debts from the filing's own frozen evidence), so the schedule captions speak in the past tense.
  const isPostFilingStatus = isPostFiling(report.status);
  const excludedExpenditures = report.expenditureItems.filter((i) => !isOnReportInclusion(i.inclusion));
  // On Ready reports the FPD-020 awaiting-settlement block (preview-driven)
  // already surfaces excluded-Pending contributions, so re-showing them here
  // would double-display — drop only that overlapping class. Voided and
  // status-drift exclusions have no other surface on Ready, so they MUST still
  // render (alert-don't-block, decision #6 — never silently drop an in-period
  // row). Filed/Amended reports carry no preview, so every excluded row shows.
  const contributionExcludedRows =
    report.status === ReportStatus.Ready
      ? excludedContributions.filter((i) => i.inclusion !== 'ExcludedPending')
      : excludedContributions;
  const showContributionExcluded = contributionExcludedRows.length > 0;

  // In-period transactions still Pending at filing are legitimately NOT on the
  // filing (Alabama two-axis model: report-Filed ≠ rows-Reported, ADR-020). While
  // any remain, the celebratory success panel would be a false "all done", so we
  // suppress it and show an actionable warning in its place. Single source of
  // truth (the same `inclusion` flag the Pending-at-filing list reads), shared
  // with PostFileSuccessPanel's mirror guard so the two can never diverge.
  const pendingAtFiling = pendingAtFilingCount(report);
  // §32 — some pending rows CAN'T be exported (AL In-Kind "Other": the portal needs
  // a description the EDI can't carry, so the builder omits them). Export says
  // "nothing to export" for these — the remedy is portal hand-entry, not
  // export + upload. Split the pending set so each subset gets the right
  // instruction: following the "Export…" banner for a manual-portal row is a
  // silent dead-end loop. requiresManualPortalEntry is server-computed by the
  // report's resolved builder, so this is jurisdiction-driven (never true for
  // federal e-file). Manual-portal rows are always contributions (in-kind), a
  // subset of the total pending count.
  const manualPortalPending = report.contributionItems.filter(
    (i) => i.inclusion === 'ExcludedPending' && i.requiresManualPortalEntry,
  ).length;
  const emittablePending = pendingAtFiling - manualPortalPending;

  // §32 — the CONFIRMED counterpart of the pending manual-portal split above. A
  // Confirmed in-kind "Other" row is "Done" (green check) and reads "will be on the
  // filing", but the AL EDI omits it, so Export silently no-ops. These rows get the
  // dedicated hand-entry panel (values + per-field copy + one-click mark-reported).
  // Keyed on ledgerStatus === Confirmed so a row already hand-keyed and marked
  // Reported drops out (requiresManualPortalEntry stays true regardless of status).
  const manualPortalConfirmed = report.contributionItems.filter(
    (i) => i.requiresManualPortalEntry && i.ledgerStatus === 'Confirmed',
  );

  // FLW-004 — the post-file success panel owns the re-download + receipt
  // affordances during the "what now" moment (Filed, no receipt recorded yet),
  // so the Electronic Submission card drops its duplicate copies to avoid two
  // download buttons + two receipt editors on screen. Once a receipt lands the
  // panel hides and the card resumes ownership. Mirrors PostFileSuccessPanel's
  // own visibility gate. Also suppressed while transactions are Pending at filing
  // (the warning card owns that state instead).
  // A proofOfFiling report (Mississippi) never gets this panel: its "what now" is the receipt chase,
  // and the panel's email branch carries North Carolina's self-send / CRO-1000 steps.
  const showSuccessPanel =
    report.status === ReportStatus.Filed &&
    !report.stateFilingReceiptId &&
    pendingAtFiling === 0 &&
    !isRemindOnly &&
    !receiptIsProofOfFiling;
  // The warning that replaces the success panel at the same position: same Filed
  // + no-receipt window, but with pending transactions still off the filing.
  // Suppressed for RemindOnly (its remedy is portal hand-entry, not export+upload).
  // Gated to the portal-only (append-only) workflow: the banner's remedy is
  // "export the new transactions and upload them to the portal", which is an
  // AL append-only-portal (ADR-020) concept. Federal's self-contained upload
  // has no export→portal remedy, so the banner (and its "Alabama FCPA portal"
  // copy) must never fire for a federal filer.
  const showPendingAtFilingWarning =
    report.status === ReportStatus.Filed &&
    !report.stateFilingReceiptId &&
    pendingAtFiling > 0 &&
    !isRemindOnly &&
    isPortalOnlyWorkflow;

  // §E — File Report and Export Delta are for the non-portal-only (federal)
  // path only. Portal-only (Alabama) uses PortalOnlySubmissionSteps instead,
  // which hides File Report and wraps Export in the three-step gated flow.
  //
  // #132 — the finalizing File action waits for the serving model to LOAD. Before the
  // compliance profile resolves, `uploadIsFiling` is undefined, so a VA (whole-report
  // portal-upload) report would momentarily fall through to this federal branch and flash
  // the finalizing "File" button before routing to its non-finalizing Generate action. Gate
  // it on the profile being present so a treasurer never sees a finalizing control that
  // contradicts VA's generate ≠ file model. (`profile` is defined once loaded — undefined
  // only mid-load; a loaded-but-partial profile still renders, so this is load-gating only.)
  const servingModelLoaded = profile !== undefined;
  const fileSubmissionBlock = report.status === ReportStatus.Ready && servingModelLoaded ? (
    <FilePackageButton
      report={report}
      stateCode={report.stateCode}
      variant="primary"
    />
  ) : null;
  const exportSubmissionBlock = isPreFiling ? (
    <ExportDeltaButton report={report} stateCode={report.stateCode} />
  ) : null;

  return (
    // In summary-first mode the whole report is capped to a document measure and centered, so
    // every section (banners, balances, submission, the Form 3 cover) shares one width and one
    // centerline instead of a full-width chrome sitting above a narrower centered summary. The
    // flat view keeps its full-width layout unchanged.
    <div className={cn('space-y-6', summaryFirstLayout && 'mx-auto max-w-3xl')}>
      {/* Back button */}
      <button
        onClick={() => navigate({ to: '/admin/finance/reports' })}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to reports
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold">{report.name}</h2>
            {isExternallyFiled ? (
              <span
                className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
                data-testid="report-external-label"
                title={externalLabelTooltip(profile?.stateDisplayName ?? null)}
              >
                {FilingLockCopy.ExternalLabel}
              </span>
            ) : (
              <span
                className={cn('rounded-full px-2 py-0.5 text-xs font-medium', badge.className)}
                data-testid="report-status-badge"
              >
                {badge.label}
              </span>
            )}
            {lock && (
              <span
                className={cn('rounded-full px-2 py-0.5 text-xs font-medium', lock.className)}
                data-testid="report-lock-badge"
                title={lock.tooltip}
              >
                {lock.label}
              </span>
            )}
            {report.amendmentNumber > 0 && (
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
                Amendment #{report.amendmentNumber}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatDate(report.periodStart)} – {formatDate(report.periodEnd)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* "Pick up changes" — only on pre-filing reports. A Filed/Amended
              report is a frozen snapshot; refreshing would be a no-op at best
              and misleading at worst, so it's hidden there. */}
          {preFilingEditable && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              data-testid="refresh-report-button"
              title="This report reflects your latest data each time it loads. If you just edited a transaction, refresh to pick up the change."
            >
              <RefreshCw className="mr-1 h-4 w-4" />
              Refresh
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAuditPanel(true)}
            title="View change history"
          >
            <Clock className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Issue #690 — an amendment says what it changed, before anything else on the page: every
          banner, schedule and total below is read differently once you know this report is a
          correction and which filing it replaces. Renders nothing on an original report. */}
      <AmendmentDiffPanel diff={report.amendmentDiff} />

      <FilingHistory report={report} />

      <SigningEvidencePanel report={report} />

      {/* Issue #138 — blocking, schema-required problems (RED) surface ABOVE the amber soft-gate
          warnings, so a treasurer who trusts the report page's banners sees what would make the
          portal REJECT the file without first opening the File modal. Same drift-proof
          HardFailureList the File modal and close hub render; it returns null when empty. */}
      <HardFailureList hardFailures={report.blockingIssues} />

      {/* Soft gate warnings */}
      {report.warnings.length > 0 && (
        <div className="space-y-2">
          {report.warnings.map((warning, idx) => (
            <WarningBanner
              key={idx}
              warning={warning}
              reportId={report.id}
              periodStart={report.periodStart}
              periodEnd={report.periodEnd}
            />
          ))}
        </div>
      )}

      {/* ADR-034 — the Form 3 summary was in scope but its rollup threw on broken
          data, so the payload degraded to the flat view. Loud but non-blocking: the treasurer
          keeps working, but the SAME bad data will very likely block filing, so we surface it
          prominently and point them at their transactions. `federalSummaryDegraded` is only ever
          true for a build failure (never a benign out-of-scope null), so this fires exactly when
          it should. */}
      {report.federalSummaryDegraded && (
        <div
          className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-red-800"
          data-testid="form3-summary-degraded-banner"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="flex-1 text-sm">
            <p className="font-semibold">We couldn&apos;t build the Form 3 summary for this report.</p>
            {report.federalSummaryDegradedDetail ? (
              // Issue #117 — the build failure is attributable to one record: name it, say why, and
              // deep-link to it instead of giving generic "review everything" advice.
              <>
                <p className="mt-0.5">
                  {degradedRecordLabel(report.federalSummaryDegradedDetail, report)} has{' '}
                  {report.federalSummaryDegradedDetail.reason}, which couldn&apos;t be rolled up onto
                  the Form 3 cover. The same problem will very likely block filing — fix this record
                  before you file.
                </p>
                <button
                  type="button"
                  onClick={() => navigateToDegradedRecord(report.federalSummaryDegradedDetail!)}
                  className="mt-1 text-xs underline"
                  data-testid="form3-summary-degraded-fix"
                >
                  Fix this record
                </button>
              </>
            ) : (
              // No attributable row (a rollup-level throw) — fall back to today's general advice.
              <p className="mt-0.5">
                Something in this period&apos;s transactions couldn&apos;t be rolled up onto the
                Form 3 cover. The same problem will very likely block filing — review your
                contributions and expenditures for this period before you file.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ADR-019 D4 — a manually-reopened period shows a banner until it auto-relocks
          on the next file. */}
      {standing.kind === 'superseded' && (
        <div
          className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
          data-testid="superseded-notice"
        >
          {supersededNotice(standing.head?.amendmentNumber ?? null)}
          {standing.head && (
            <>
              {' '}
              <Link
                to="/admin/finance/reports/$reportId"
                params={{ reportId: standing.head.complianceReportId }}
                className="font-medium underline"
              >
                Go to Amendment #{standing.head.amendmentNumber}
              </Link>
            </>
          )}
        </div>
      )}

      {standing.kind === 'amendmentInProgress' && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="amendment-in-progress-notice"
        >
          {amendmentInProgressNotice(standing.amendment.amendmentNumber)}{' '}
          <Link
            to="/admin/finance/reports/$reportId"
            params={{ reportId: standing.amendment.complianceReportId }}
            className="font-medium underline"
          >
            Continue Amendment #{standing.amendment.amendmentNumber}
          </Link>
        </div>
      )}

      {inForce && report.manuallyUnlocked && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="reopened-banner"
        >
          {FilingLockCopy.ReopenedBanner}
        </div>
      )}

      {/* ADR-019 — the neutral "Locked" pill is otherwise unexplained, so a locked
          period gets a visible one-line explanation (mirroring the reopened-banner
          above; the two states are mutually exclusive). The Reopen affordance below
          is the one-action remedy. Suppressed for externally-filed reports — their
          external label already carries the frozen/no-repackage explanation. */}
      {inForce && report.locked && !isExternallyFiled && (
        <div
          className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
          data-testid="locked-banner"
        >
          {report.isAmendment && !isPostFiling(report.status)
            ? FilingLockCopy.LockedAmendmentBanner
            : FilingLockCopy.LockedBadgeTooltip}
        </div>
      )}

      {/* Open-but-already-filed soft nudge (a nudge, not a block). §49 — the copy
          is workflow-aware: a PortalOnly (append-only) jurisdiction warns that
          amendments happen in the portal and re-uploading would double every row;
          federal keeps the plain re-file framing. */}
      {showOpenAlreadyFiledNudge && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"
          data-testid="open-already-filed-nudge"
        >
          {openAlreadyFiledNudge(amendmentModel, report.stateCode)}
        </div>
      )}

      {/* ADR-019 D4 — the easy-but-audited reopen / re-lock affordance (locked or
          reopened periods only; gated on Finance/Manage inside the component). A locked
          treasurer-attested report reaches the unlock through ReopenReportAffordance's
          "did you file this with the state?" instead, so it never shows two Reopen buttons. */}
      {inForce && !(report.canReopen && report.locked) && <ReopenPeriodAffordance report={report} />}

      {/* Balance summary. When the federal or AL summary-first cash bridge is on the page it
          is the single ending-cash figure, so the standalone Ending Balance tile is suppressed
          here to avoid showing the ending twice. The editable Beginning Balance affordance
          (FCG-009) stays either way. */}
      <div
        className={cn(
          'grid gap-4 rounded-lg border p-4',
          hideEndingBalanceTile ? 'grid-cols-1' : 'grid-cols-2',
        )}
      >
        <div>
          <p className="text-sm text-muted-foreground">Beginning Balance</p>
          {/* FCG-009 — editable on pre-filing (Ready/Overdue) reports; frozen
              read-only once Filed/Amended (the figure lives in the as-filed snapshot). */}
          <BeginningBalanceEditor
            reportId={report.id}
            beginningBalance={report.beginningBalance}
            concurrencyVersion={report.concurrencyVersion}
            editable={report.status === ReportStatus.Ready}
          />
        </div>
        {!hideEndingBalanceTile && (
          <div>
            <p className="text-sm text-muted-foreground">Ending Balance</p>
            <p className="text-lg font-semibold">{formatAmount(report.endingBalance)}</p>
          </div>
        )}
      </div>

      {/* GitHub #778 — final-report flag. Offered off the DECLARED `profile.supportsFinalReport`
          axis (served from the ruleset filing profile), never a `stateCode === 'LA'` string check
          (finance golden rule — jurisdiction-variant behavior is a declared policy, not a
          state-named boolean). LA is the only jurisdiction that declares it today; a jurisdiction
          whose builder consumes the flag flips it true with no change here. Undefined while the
          profile loads → not offered (safe). Editable on Ready only — a filed period's cover
          replays it on an amendment. */}
      {profile?.supportsFinalReport === true && (
        <div className="rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Final report</p>
          <FinalReportEditor
            reportId={report.id}
            finalReportReason={report.finalReportReason}
            concurrencyVersion={report.concurrencyVersion}
            editable={report.status === ReportStatus.Ready}
          />
        </div>
      )}

      {/* The in-app cover review — offered off the DECLARED `profile.inAppCoverReview` axis (Mississippi
          today: its builder reads the stored corrections), never a `stateCode === 'MS'` check. Pre-filing
          only: the review re-derives the cover as of now, so on a filed report it would not show what was
          filed — the filed package's own cover is the record there. */}
      {profile?.inAppCoverReview === true && !isPostFiling(report.status) && (
        <div className="rounded-lg border p-4 space-y-2" data-testid="ms-cover-review-card">
          <p className="text-sm text-muted-foreground">Cover page</p>
          <p className="text-sm">
            Check the values the app filled in on the official cover — and correct any — before you
            download or send it.
          </p>
          <MississippiCoverReviewButton reportId={report.id} />
        </div>
      )}

      {/* Issue #643 — warn-before notice: editing an earlier period ripples its
          opening balance forward. Only rendered on editable (Ready) reports, and only
          when a downstream ripple actually exists (the hook returns empty otherwise). */}
      {report.status === ReportStatus.Ready && (
        <CashRecalcRippleNotice editDate={report.periodEnd} />
      )}

      {/* The receipt chase (proofOfFiling — Mississippi): confirmed filed, no receipt recorded, not
          dismissed. Server-derived, so it can't drift from the recorded dismissal decision. */}
      {report.receiptChaseOpen && <ReceiptChaseOpenItem report={report} officeName={portalName} />}
      {/* Edit-after-send: once emailed, the sent file is the office's record and there is no partial
          amendment. Ships with the proofOfFiling policy, where that sent file is the filing of record. */}
      {isPreFiling && receiptIsProofOfFiling && (
        <SentReportEditWarning reportId={report.id} officeName={portalName} />
      )}

      {/* FIN-011: Electronic submission panel. ADR-018 — an externally-filed report
          has no in-system package, so every package-only surface (download / audit
          packet / signing evidence / re-file) is suppressed; the provenance note
          stands in their place so a graceful-but-confusing 404 is never reached. */}
      <div className="rounded-lg border p-4 space-y-4">
        <h3 className="text-base font-semibold">Electronic Submission</h3>
        {/* ADR-023 — once a report is Filed via PortalAttested (canReopen), the only
            forward move is Reopen (the report-level committing attestation's symmetric
            undo). Self-hides otherwise; shown across the external/remind/normal branches. */}
        <ReopenReportAffordance report={report} amendmentModel={amendmentModel} />
        {filingTransport === 'agentEntry' && isPreFiling && !isExternallyFiled && (
          <PortalRunPanel
            reportId={report.id}
            concurrencyVersion={report.concurrencyVersion}
            portalName={portalName}
            noticeOf={noticeOf}
          />
        )}
        {isExternallyFiled ? (
          <p className="text-sm text-muted-foreground" data-testid="external-filed-note">
            {externalLabelTooltip(profile?.stateDisplayName ?? null)}
          </p>
        ) : isRemindOnly ? (
          /* RemindOnly (the AL annual): the app builds no file — Export / File
             Report both 400. Render a reminder + the attestation path only. The
             jurisdiction name comes from `portalName` (off the report's own
             stateCode), never a hardcoded "Alabama" or the mocked profile name. */
          <div className="space-y-4" data-testid="remind-only-submission">
            <p className="text-sm text-muted-foreground" data-testid="remind-only-note">
              This report is filed directly in the {portalName} — we don&apos;t prepare a
              file for it. File it in the {portalName}, then mark it filed here.
            </p>
            {/* ADR-023 — hide mark-filed once Filed via portal (canReopen); Reopen is the
                only forward move then (rendered above). isPreFiling already excludes Filed,
                so this is belt-and-suspenders against a canReopen+editable race. */}
            {isPreFiling && !report.canReopen && (
              <div className="space-y-1">
                <MarkFiledInPortalButton
                  report={report}
                  stateCode={report.stateCode}
                  onRecorded={setRecordedFiledNote}
                />
                <p className="text-sm text-muted-foreground" data-testid="remind-only-mark-desc">
                  Record that you already filed it in the {portalName}.
                </p>
              </div>
            )}
            {recordsFilingReceipt &&
              (report.status === ReportStatus.Filed || report.status === ReportStatus.Amended) && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                    State Filing Receipt
                  </p>
                  <ReceiptEditor
                    reportId={report.id}
                    receiptId={report.stateFilingReceiptId}
                    receiptAt={report.stateFilingReceiptAt}
                    concurrencyVersion={report.concurrencyVersion}
                  />
                </div>
              )}
          </div>
        ) : (
        <>
        {/* Filing models, routed by serving model (no `stateCode === 'VA'` branch):
            • §46 portal-only / append-only (AL, Incremental): a gated three-step flow
              (Export → Confirm upload → Mark filed). File Report is hidden — uploading a
              full package to an append-only portal would duplicate every transaction.
            • #132 / #482 whole-report portal upload (uploadIsFiling=false — every jurisdiction now):
              generate the whole document (non-finalizing, generate ≠ file), get it into the state's
              system, then Mark filed. The generate control branches on `certifyAtGenerate`: VA/NC use
              a plain non-attesting Generate; federal uses the attestation File flow (its `.fec` embeds
              the certification) which stays Ready, then the treasurer uploads to efo.fec.gov and marks
              it filed with the FEC filing id.
            • the else branch is the load-gating fallback (profile not yet resolved) — a plain File /
              Export layout; no jurisdiction ships uploadIsFiling=true today.
            #545 — while formats are still unsettled for a stateful pre-filing report, render nothing
            rather than flashing the non-portal-only default (mirrors `servingModelLoaded` gating
            `fileSubmissionBlock` to null). Filed/Amended reports (`isPreFiling` false) are never
            gated — they don't depend on the resolved workflow. */}
        {isPreFiling && !servingModelSettled ? (
          profileUnready && (
            <p role="alert" data-slot="report-profile-unready" className="text-sm text-destructive">
              {profileUnready} The filing steps for this report depend on how your state takes filings.
            </p>
          )
        ) : isIncrementalPortalWorkflow && isPreFiling ? (
          <PortalOnlySubmissionSteps
            report={report}
            stateCode={report.stateCode}
            portalName={portalName}
            onRecorded={setRecordedFiledNote}
          />
        ) : isWholeReportPortalUpload && isPreFiling ? (
          <WholeReportPortalUploadSteps
            report={report}
            filingDestination={filingDestination}
            isEmailFiling={isEmailFiling}
            certifyAtGenerate={certifyAtGenerate}
            officeName={portalName}
            stateName={submissionStateName}
            captureStateReceipt={receiptIsProofOfFiling}
            onRecorded={setRecordedFiledNote}
            separateSignedCover={profile?.separateSignedCover === true}
          />
        ) : (
          <>
            {fileSubmissionBlock}
            {exportSubmissionBlock}
            {isPreFiling && !report.canReopen && (
              <MarkFiledInPortalButton
                report={report}
                stateCode={report.stateCode}
                captureStateReceipt={receiptIsProofOfFiling}
                onRecorded={setRecordedFiledNote}
              />
            )}
            <ExportUploadGateBanner reportId={report.id} liveExportDraft={report.liveExportDraft} />
          </>
        )}
        {(report.status === ReportStatus.Filed || report.status === ReportStatus.Amended) && (
          <>
            {recordedFiledNote && (
              <p className="mb-2 text-sm text-green-700" data-testid="mark-filed-recorded-note">
                {recordedFiledNote}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {/* While the success panel is up it owns the re-download button
                  (FLW-004); the card surfaces only the distinct audit packet. */}
              {!showSuccessPanel && (
                <>
                  <PendingButton
                    variant="outline"
                    size="sm"
                    pending={downloading}
                    icon={<Download className="h-4 w-4" />}
                    onClick={handleDownload}
                    data-testid="download-package-button"
                  >
                    {downloading ? 'Downloading...' : 'Download Package'}
                  </PendingButton>
                  {downloadError && (
                    <span className="text-sm text-destructive" data-testid="download-error">
                      {downloadError}
                    </span>
                  )}
                </>
              )}
              {/* FPD-026 — audit-packet ZIP for CPA audits. Gated on
                  Finance.Manage inside the button; renders null when
                  the treasurer lacks the capability. */}
              <DownloadAuditPacketButton reportId={report.id} />
            </div>
            {/* The success panel owns the receipt editor while it's up (FLW-004). */}
            {!showSuccessPanel && recordsFilingReceipt && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                  State Filing Receipt
                </p>
                <ReceiptEditor
                  reportId={report.id}
                  receiptId={report.stateFilingReceiptId}
                  receiptAt={report.stateFilingReceiptAt}
                  concurrencyVersion={report.concurrencyVersion}
                />
              </div>
            )}
          </>
        )}
        </>
        )}
      </div>

      {/* Pending-at-filing warning — occupies the success panel's position when
           in-period transactions are still off the filing. Never celebrates a
           period that isn't actually reported; points the treasurer at the export
           + portal upload (ADR-020). Non-blocking. Suppressed for an
           externally-filed report (no in-system export path). */}
      {!isExternallyFiled && showPendingAtFilingWarning && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4"
          data-testid="pending-at-filing-warning"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-amber-900">
                {PreflightCopy.PendingAtFilingWarningTitle}
              </p>
              {/* §32 — emittable pending rows keep the "export + upload" remedy;
                   un-emittable In-Kind "Other" rows get the manual-portal remedy.
                   Both render when both kinds are present. */}
              {emittablePending > 0 && (
                <p className="text-sm text-amber-800" data-testid="pending-at-filing-emittable">
                  {pendingAtFilingWarningBody(emittablePending, submissionStateName, portalName)}
                </p>
              )}
              {manualPortalPending > 0 && (
                <p className="text-sm text-amber-800" data-testid="pending-at-filing-manual-portal">
                  {pendingAtFilingManualPortalBody(
                    manualPortalPending,
                    submissionStateName,
                    portalName,
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* §32 — Confirmed in-kind "Other" hand-entry panel. Renders above the
           schedules (an action surface, like the pending-at-filing warning) whenever
           a ready-to-file row can't ride the EDI upload. Self-gates on the flag, so
           it only appears for a portal-upload jurisdiction that actually has one. */}
      {manualPortalConfirmed.length > 0 && (
        <ManualPortalEntryPanel
          rows={manualPortalConfirmed}
          reportId={report.id}
          stateDisplayName={submissionStateName}
          portalName={portalName}
        />
      )}

      {/* FLW-004 — post-file success panel. Renders above the schedule
           sections so the treasurer sees the "what now" before scrolling into
           the data. Self-gates on Filed + no-receipt (+ nothing Pending at
           filing); reuses the existing package-download handler. ADR-018 —
           suppressed for an externally-filed report (no in-system package to
           re-download). */}
      {!isExternallyFiled && !isRemindOnly && !receiptIsProofOfFiling && (
        <PostFileSuccessPanel
          report={report}
          onReDownload={handleDownload}
          downloading={downloading}
          onDownloadCover={handleDownloadCover}
          downloadingCover={downloadingCover}
          filingTransport={filingTransport}
          amendmentModel={amendmentModel}
        />
      )}

      {/* GitHub #34 — the durable per-report portal-follow-up checklist. Renders here
           (below the post-file success moment, above the schedules) so a treasurer who
           returns to a Filed report still sees the manual portal actions they owe. Self-
           gates on having rows, so it's invisible for reports with no portal handoffs
           (e.g. a clean federal filing, or the externally-filed backfill). */}
      <PortalFollowUpsChecklist reportId={report.id} />

      {/* ADR-034 / #107 — summary-first REPLACES the flat Contributions/Expenditures
          schedules with the Form 3 Detailed Summary Page + cash bridge, per-line drill-down
          (expand a line to see the transactions behind it, reusing the editable items so
          click-through-to-edit + fix-missing-donor-info still work), the read-only Schedule C
          loans, and a single "in this period, not on this filing" section. A null summary keeps
          the flat view below unchanged (see the degradation banner above for a build-failure null). */}
      {/* #304 — NC summary-first: the CFRS Detailed Summary (CRO-1100) cover — reporting-period
          and election-cycle columns side by side — rendered off the derived totals #294 populated,
          ABOVE the itemized schedules. Unlike the federal/VA/AL views (which fold their itemized
          rows into an in-view drill-down and so REPLACE the flat tables), the NC cover is a pure
          roll-up, so the flat itemized schedules stay below it (the NC case falls through to Flat) —
          cover then schedules, the shape of the paper NC report. */}
      {report.summaryKind === ReportSummaryKind.NorthCarolina && report.northCarolinaSummary && (
        <NorthCarolinaSummaryView summary={report.northCarolinaSummary} />
      )}

      {/* ADR-051 — the single view dispatch, keyed on the server-resolved discriminator.
          `assertNever` makes a new ReportSummaryKind a compile error until it's registered
          here, so onboarding a state is one case in one place (mirrored by the backend
          resolver). Inner blocks are unchanged from the retired ternary. */}
      {(() => {
        switch (report.summaryKind) {
          case ReportSummaryKind.Federal:
            return (
        <Form3SummaryView
          summary={report.federalSummary!}
          contributionItems={report.contributionItems}
          expenditureItems={report.expenditureItems}
          requiresEmployer={profile?.employerOccupationRequired ?? false}
          asFiled={isPostFilingStatus}
          onEditContribution={preFilingEditable ? navigateToEditContribution : null}
          onEditExpenditure={preFilingEditable ? navigateToEditExpenditure : null}
          onFixMissingInfo={setEditingContactId}
        />
            );
          case ReportSummaryKind.Virginia:
            return (
        /* Issue #141 — VA summary-first: COMET Statement-of-funds + cash bridge + expandable
           Schedules A/C/D/E/F, replacing the flat tables (same treatment federal gets). */
        <VaCometSummaryView
          vaSchedules={report.vaSchedules!}
          contributionItems={report.contributionItems}
          expenditureItems={report.expenditureItems}
          refundItems={report.refundItems}
          onEditContribution={preFilingEditable ? navigateToEditContribution : null}
          onEditExpenditure={preFilingEditable ? navigateToEditExpenditure : null}
        />
            );
          case ReportSummaryKind.Alabama:
            return (
        /* Alabama summary-first: Statement-of-funds + cash bridge + expandable Schedule A/B/D,
           replacing the flat tables (same treatment federal + VA get). */
        <AlabamaSummaryView
          report={report}
          preview={preview ?? null}
          portalName={portalName}
          requiresEmployer={profile?.employerOccupationRequired ?? false}
          onEditContribution={preFilingEditable ? navigateToEditContribution : null}
          onEditExpenditure={preFilingEditable ? navigateToEditExpenditure : null}
          onFixMissingInfo={setEditingContactId}
        />
            );
          // #304 — NC is summary-first via the CRO-1100 cover rendered ABOVE this switch (a pure
          // roll-up with no per-item drill-down), so it falls through to the SAME flat itemized
          // schedules — the cover then the schedules, mirroring the paper NC report.
          case ReportSummaryKind.NorthCarolina:
          case ReportSummaryKind.Flat:
            return (
        <>
      {/* Contributions (neutral state-filer heading — FLW-002) */}
      <div>
        <h3 className="text-lg font-semibold">
          Contributions: {scheduleACount} items, {formatAmount(scheduleATotal)}
        </h3>
        {/* FPD-020 / Issue #146 — dual-count header for Ready reports. "In this
             period" includes every Pending row that hasn't been attested yet
             (itemized + unreconciled); "Will be on the filing" reads the
             server-authored will-file projection (the file's own inclusion gate),
             so it equals what actually files — not a client-derived `itemized.count`
             that drifts on amendments / processor-status drift. When they differ, a
             subtitle directs the treasurer to the Heads-up flow. */}
        {report.status === ReportStatus.Ready && preview && (
          <ScheduleADualCountHeader
            inPeriodCount={preview.itemized.count + preview.unreconciled.count}
            inPeriodTotal={preview.itemized.total + preview.unreconciled.total}
            willBeOnFilingCount={preview.willFile.count}
            willBeOnFilingTotal={preview.willFile.total}
          />
        )}
        {report.scheduleASummary.length > 0 && (
          <div className="mt-2 flex gap-4 text-sm text-muted-foreground">
            {report.scheduleASummary.map((s) => (
              <span key={s.type}>{s.type}: {s.count} ({formatAmount(s.total)})</span>
            ))}
          </div>
        )}
        {/* Itemized contributions — donors at/over the itemization threshold, who
             carry donor identity on the filing. Scoped to the Included (filed) set;
             excluded rows are demarcated below. Server-computed split (isItemized),
             never recomputed here — jurisdiction-agnostic (AL emits the non-itemized
             rows as coded records; FEC rolls them into an aggregate). */}
        {itemizedContributions.length > 0 && (
          <div className="mt-3" data-testid="itemized-contributions-section">
            <p className="text-sm font-medium">
              {PreflightCopy.ScheduleItemizedContributionsHeading} — {itemizedContributions.length} items,{' '}
              {formatAmount(itemizedContributionTotal)}
            </p>
            <ContributionScheduleTable
              rows={itemizedContributions}
              portalName={portalName}
              requiresEmployer={profile?.employerOccupationRequired ?? false}
              onFixMissingInfo={setEditingContactId}
              onEditTransaction={preFilingEditable ? navigateToEditContribution : null}
            />
          </div>
        )}

        {/* Non-itemized contributions — donors under the itemization threshold that
             file as a combined total. Unlike the expenditure non-itemized aggregate
             box, contributions get a REAL inspectable list: the treasurer can see (and
             open) each row to verify or edit it. The header carries the count +
             subtotal so the combined figure is legible at a glance; the rows below use
             the same table (so they reflow for large/bold font like the itemized set). */}
        {nonItemizedContributionCount > 0 && (
          <div
            className="mt-4 rounded border border-dashed p-3"
            data-testid="non-itemized-contributions-list"
          >
            <p className="text-sm font-medium">
              {PreflightCopy.ScheduleNonItemizedContributionsHeading} — {nonItemizedContributionCount} items,{' '}
              {formatAmount(nonItemizedContributionTotal)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {PreflightCopy.ScheduleNonItemizedContributionHelpLine}
            </p>
            <ContributionScheduleTable
              rows={nonItemizedContributions}
              portalName={portalName}
              requiresEmployer={profile?.employerOccupationRequired ?? false}
              onFixMissingInfo={setEditingContactId}
              onEditTransaction={preFilingEditable ? navigateToEditContribution : null}
            />
          </div>
        )}
        {/* ADR-018 item 2 — the frozen non-itemized remainder (AL form line 2b) for an
             externally-filed report. The itemized rows above + this line equal the frozen
             total to the cent, so the displayed form foots the way the state form does.
             The backend computes this UNCLAMPED: a negative means the itemized rows exceed
             the frozen total (a backfill data error), so we render the line (never hide it)
             with an alert style instead of swallowing the discrepancy. */}
        {isExternallyFiled && report.nonItemizedContributions !== 0 && (
          <div
            className={cn(
              'mt-3 rounded border border-dashed p-3',
              report.nonItemizedContributions < 0 && 'border-destructive/30 bg-destructive/5',
            )}
            data-testid="non-itemized-contributions-line"
          >
            <p
              className={cn(
                'text-sm font-medium',
                report.nonItemizedContributions < 0 && 'text-destructive',
              )}
            >
              {FilingLockCopy.NonItemizedContributionsLabel} —{' '}
              {formatAmount(report.nonItemizedContributions)}
            </p>
            {report.nonItemizedContributions < 0 ? (
              <p
                className="mt-1 text-xs font-medium text-destructive"
                data-testid="non-itemized-contributions-negative-note"
              >
                {FilingLockCopy.NonItemizedNegativeWarning}
              </p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">{FilingLockCopy.NonItemizedHelpLine}</p>
            )}
          </div>
        )}
        {/* FLW-002 — excluded-but-in-period contributions. Demarcated, not
             hidden (decision #10). Labels state file-time facts. */}
        {showContributionExcluded && (
          <ExcludedRowsSubsection
            rows={contributionExcludedRows.map((i) => ({
              id: i.id,
              date: i.date,
              name: i.contactName,
              amount: i.amount,
              inclusion: i.inclusion,
              requiresManualPortalEntry: i.requiresManualPortalEntry,
            }))}
            portalName={portalName}
            testIdPrefix="excluded-contribution"
          />
        )}
        {/* FPD-020 — Awaiting-settlement rows. In-period Pending contributions
             whose attestation hasn't been recorded. Each carries an
             "Awaiting settlement" badge per the AC. */}
        {report.status === ReportStatus.Ready && preview && preview.unreconciled.items.length > 0 && (
          <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm font-medium text-amber-900">
              Awaiting settlement ({preview.unreconciled.count})
            </p>
            <ul className="mt-2 space-y-1 text-sm text-amber-950">
              {preview.unreconciled.items.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center gap-2"
                  data-testid={`awaiting-settlement-${item.id}`}
                >
                  <span>{formatDate(item.date)}</span>
                  <span>{item.contactName}</span>
                  <span>{formatAmount(item.amount)}</span>
                  <span
                    title={PreflightCopy.ScheduleAwaitingSettlementBadgeTooltip}
                    className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-900"
                  >
                    {PreflightCopy.ScheduleAwaitingSettlementBadge}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Expenditures (neutral state-filer heading — FLW-002) */}
      <div>
        <h3 className="text-lg font-semibold">
          Expenditures: {scheduleBCount} items, {formatAmount(scheduleBTotal)}
        </h3>

        {/* Itemized expenditures — one row per expenditure (synthetic fee
            rollups render here like any row, marked "(estimated)"). */}
        {itemizedExpenditures.length > 0 && (
          <div className="mt-3">
            <p className="text-sm font-medium">
              {PreflightCopy.ScheduleItemizedExpendituresHeading} — {itemizedExpenditures.length} items,{' '}
              {formatAmount(itemizedExpenditureTotal)}
            </p>
            <ExpenditureScheduleTable
              rows={itemizedExpenditures}
              reportId={report.id}
              onEditTransaction={preFilingEditable ? navigateToEditExpenditure : null}
            />
          </div>
        )}

        {/* Non-itemized expenditures — below-threshold / incomplete-payee-info rows
            that file as a combined total. Like the non-itemized CONTRIBUTIONS section,
            this is a REAL inspectable list (not a rolled-up box): the treasurer can
            open each row to verify it, or add the payee's name/address to itemize it.
            The header carries the count + subtotal so the combined figure is legible
            at a glance; the rows below use the same table (so they reflow for
            large/bold font like the itemized set). */}
        {nonItemizedCount > 0 && (
          <div
            className="mt-4 rounded border border-dashed p-3"
            data-testid="non-itemized-expenditures-list"
          >
            <p className="text-sm font-medium">
              {PreflightCopy.ScheduleNonItemizedExpendituresHeading} — {nonItemizedCount} items,{' '}
              {formatAmount(nonItemizedTotal)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {PreflightCopy.ScheduleNonItemizedExpenditureHelpLine}
            </p>
            <ExpenditureScheduleTable
              rows={nonItemizedExpenditures}
              reportId={report.id}
              onEditTransaction={preFilingEditable ? navigateToEditExpenditure : null}
            />
          </div>
        )}

        {/* ADR-018 item 2 — the frozen non-itemized remainder (AL form line 5b) for an
            externally-filed report: a single total with no per-row backfill, so itemized
            rows + this line equal the frozen total. Distinct from the in-system per-row
            aggregate above (which an ExternalPortal report never has). Computed UNCLAMPED:
            a negative means the itemized rows exceed the frozen total (a backfill data
            error), so we render it (never hide) with an alert style. */}
        {isExternallyFiled && report.nonItemizedExpenditures !== 0 && (
          <div
            className={cn(
              'mt-4 rounded border border-dashed p-3',
              report.nonItemizedExpenditures < 0 && 'border-destructive/30 bg-destructive/5',
            )}
            data-testid="non-itemized-expenditures-line"
          >
            <p
              className={cn(
                'text-sm font-medium',
                report.nonItemizedExpenditures < 0 && 'text-destructive',
              )}
            >
              {FilingLockCopy.NonItemizedExpendituresLabel} —{' '}
              {formatAmount(report.nonItemizedExpenditures)}
            </p>
            {report.nonItemizedExpenditures < 0 ? (
              <p
                className="mt-1 text-xs font-medium text-destructive"
                data-testid="non-itemized-expenditures-negative-note"
              >
                {FilingLockCopy.NonItemizedNegativeWarning}
              </p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">{FilingLockCopy.NonItemizedHelpLine}</p>
            )}
          </div>
        )}

        {/* FLW-002 — excluded-but-in-period expenditures (Voided, status
             drift). Demarcated, not hidden. */}
        {excludedExpenditures.length > 0 && (
          <ExcludedRowsSubsection
            rows={excludedExpenditures.map((i) => ({
              id: i.id,
              date: i.date,
              name: i.contactName,
              amount: i.amount,
              inclusion: i.inclusion,
            }))}
            portalName={portalName}
            testIdPrefix="excluded-expenditure"
          />
        )}
      </div>

      {/* #136 — Refunds (Schedule D). An issued contribution refund is a disbursement that
          reduces cash-on-hand; before this it existed only in the generated file and in no
          on-screen table, so the treasurer reviewed a higher cash figure than the one they
          filed. Rendered from the same refund set the file lists on Schedule D, so the table
          and the filed disbursements are the one set. Only shown when refunds exist. */}
      {report.refundItems.length > 0 && (
        <div data-testid="refunds-schedule">
          <h3 className="text-lg font-semibold">
            Refunds: {report.refundItems.length} items,{' '}
            {formatAmount(report.refundItems.reduce((sum, r) => sum + r.amount, 0))}
          </h3>
          {/* Jurisdiction-neutral: whether a refund reduces the displayed cash-on-hand is a
              per-jurisdiction rule (VA/federal deduct it; AL treats it as a portal amendment)
              and is already reflected in the Ending Balance figure — the copy states the fact
              (a refund returned to a donor), not the COH mechanics. */}
          <p className="mt-1 text-xs text-muted-foreground">
            Contributions returned to donors during this period.
          </p>
          <RefundScheduleTable rows={report.refundItems} />
        </div>
      )}
        </>
            );
          default:
            return assertNever(report.summaryKind);
        }
      })()}

      {/* Audit Log Sheet */}
      <Sheet open={showAuditPanel} onOpenChange={setShowAuditPanel}>
        <SheetContent side="right" className="w-96 p-0">
          <AuditLogPanel
            entityType="Report"
            entityId={report.id}
            onClose={() => setShowAuditPanel(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Contact editor Sheet — opened from the row-level missing-info button. */}
      <Sheet
        open={!!editingContactId}
        onOpenChange={(open) => { if (!open) closeContactEditor(); }}
      >
        <SheetContent side="right" className="w-[640px] max-w-full p-0 overflow-y-auto">
          {editingContactId && (
            <ContactDetail contactId={editingContactId} onBack={closeContactEditor} />
          )}
        </SheetContent>
      </Sheet>

    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

// Issue #117 — a human label for the offending record behind a degraded Form 3 summary. Resolves
// the record's contact off the report items so the banner names it ("The contribution from Ada
// Adams"); falls back to a generic phrase when the row isn't among the displayed items (e.g. a
// prior-period cycle-window row that still feeds Column B).
function degradedRecordLabel(
  detail: FederalSummaryDegradedDetailDto,
  report: ComplianceReportDetailDto,
): string {
  switch (detail.entityType) {
    case 'Contribution': {
      const item = report.contributionItems.find((i) => i.id === detail.entityId);
      return item ? `The contribution from ${item.contactName}` : 'A contribution in this period';
    }
    case 'Expenditure': {
      const item = report.expenditureItems.find((i) => i.id === detail.entityId);
      return item ? `The expenditure to ${item.contactName}` : 'An expenditure in this period';
    }
    default:
      return assertNever(detail.entityType);
  }
}

// USI-005 (ADR-011) — `/close/sort` was retired; the close hub absorbed
// the workspace surfaces these warnings deep-linked into.
type WarningFixTarget =
  | { to: '/admin/finance/close'; search: Record<string, string> }
  | { to: '/admin/finance/contacts'; search: Record<string, string> };

function warningFixTarget(
  warning: SoftGateWarning,
  context: { reportId: string; periodStart: string; periodEnd: string },
): WarningFixTarget | null {
  const returnContext = { returnTo: 'report', reportId: context.reportId };
  switch (warning.type) {
    case 'UnclassifiedInPeriod':
    case 'UnresolvedOverLimit':
    // #150/ADR-038 — the fix is on the Close page's rapid-report action (Generate → Mark reported).
    case 'VirginiaRapidReportUnfiled':
    // #182 — the Louisiana rapid-report banner shares the same Close-page fix path.
    case 'LouisianaRapidReportUnfiled':
      return { to: '/admin/finance/close', search: { ...returnContext } };
    case 'MissingDonorInfo':
    // #150/ADR-038 — VA over-length values and unmappable states are per-contributor/payee fixes.
    case 'VirginiaOverLengthValue':
    case 'VirginiaUnmappableState':
    // LA over-length values are mostly per-contributor/payee fixes too. The settings-derived half
    // (committee name, office, the financial-institution block) is fixed in finance settings
    // instead, so the link is right for the common case and the warning text names the field for
    // the rest. A value that transliterates to blank (#779) is fixed the same way — add a
    // Latin-alphabet spelling to the named contact or settings field — so it shares the link.
    case 'LouisianaOverLengthValue':
    case 'LouisianaTransliteratedEmptyValue':
      return {
        to: '/admin/finance/contacts',
        search: returnContext,
      };
    default:
      return null;
  }
}

function WarningBanner({
  warning,
  reportId,
  periodStart,
  periodEnd,
}: {
  warning: SoftGateWarning;
  reportId: string;
  periodStart: string;
  periodEnd: string;
}) {
  const navigate = useNavigate();
  const isError = warning.severity === 'Error';
  const target = warningFixTarget(warning, { reportId, periodStart, periodEnd });
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg border p-3',
        isError ? 'border-red-300 bg-red-50 text-red-800' : 'border-orange-300 bg-orange-50 text-orange-800',
      )}
    >
      {isError
        ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      }
      <div className="flex-1">
        {/* Append the affected-row count only when it's meaningful — a whole-report warning
            (e.g. cycle-start-predates-data) carries no per-row count, so " (0)" would be noise. */}
        <span className="text-sm">
          {warning.message}
          {warning.count > 0 ? ` (${warning.count})` : ''}
        </span>
        {target && (
          <button
            onClick={() => {
              switch (target.to) {
                case '/admin/finance/close':
                  void navigate({ to: target.to, search: target.search });
                  return;
                case '/admin/finance/contacts':
                  void navigate({ to: target.to, search: target.search });
                  return;
              }
            }}
            className="ml-2 text-xs underline"
          >
            Fix Issues
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * FPD-020 — Renders the AC's dual-count header on Schedule A:
 * "In this period: {N} contributions ({sum})" + "Will be on the filing:
 * {N} contributions ({sum})". When the two counts differ, surfaces the
 * AC-mandated subtitle "{diff} contribution(s) need review before they'll be
 * on the filing." with a "Review contributions" link to the Heads-up flow.
 */
function ScheduleADualCountHeader({
  inPeriodCount,
  inPeriodTotal,
  willBeOnFilingCount,
  willBeOnFilingTotal,
}: {
  inPeriodCount: number;
  inPeriodTotal: number;
  willBeOnFilingCount: number;
  willBeOnFilingTotal: number;
}) {
  const diff = inPeriodCount - willBeOnFilingCount;
  return (
    <div className="mt-2 space-y-1 text-sm" data-testid="schedule-a-dual-count-header">
      <div className="flex flex-wrap gap-4 text-muted-foreground">
        <span data-testid="schedule-a-in-period">
          In this period: {inPeriodCount} contribution{inPeriodCount === 1 ? '' : 's'} ({formatAmount(inPeriodTotal)})
        </span>
        <span data-testid="schedule-a-will-be-filed">
          Will be on the filing: {willBeOnFilingCount} contribution{willBeOnFilingCount === 1 ? '' : 's'} ({formatAmount(willBeOnFilingTotal)})
        </span>
      </div>
      {diff > 0 && (
        <p className="text-xs text-amber-800" data-testid="schedule-a-review-subtitle">
          {diff} contribution{diff === 1 ? '' : 's'} need review before they'll be on the filing.
        </p>
      )}
    </div>
  );
}

// FLW-002 — treasurer-first label for an excluded row's inclusion status.
// File-time facts (decision #10): a Filed report describes the filing, not the
// row's current life, so an excluded-as-Pending row that has since been
// Confirmed still reads "Pending at filing". The `assertNever` guard keeps the
// union exhaustive.
//
// §32 — a Pending row the electronic export can't carry (AL In-Kind "Other")
// gets the "Add manually in {portal}" badge instead of "Pending at filing":
// that row's remedy is portal hand-entry, not export + upload, so the plain
// "Pending at filing" badge would point the treasurer at a dead-end. `portalName`
// is resolved per jurisdiction by the caller (never hard-coded).
function excludedInclusionLabel(
  // the subsection only ever receives genuinely off-filing rows (on-report rows,
  // Included + Reported, are filtered out before here), so the label domain excludes both.
  inclusion: Exclude<ReportRowInclusion, 'Included' | 'Reported'>,
  requiresManualPortalEntry: boolean,
  portalName: string,
): string {
  if (inclusion === 'ExcludedPending' && requiresManualPortalEntry) {
    return PreflightCopy.ScheduleAddManuallyInPortalLabel.replace('{portal}', portalName);
  }
  switch (inclusion) {
    case 'ExcludedPending':
      return PreflightCopy.ScheduleExcludedPendingLabel;
    case 'ExcludedVoided':
      return PreflightCopy.ScheduleExcludedVoidedLabel;
    case 'ExcludedStatusDrift':
      return PreflightCopy.ScheduleExcludedStatusDriftLabel;
    default:
      return assertNever(inclusion);
  }
}

interface ExcludedRow {
  id: string;
  date: string;
  name: string;
  amount: number;
  inclusion: ReportRowInclusion;
  // §32 — contributions only (expenditures never carry it). Undefined on the
  // expenditure subsection ⇒ the plain inclusion label.
  requiresManualPortalEntry?: boolean;
}

/**
 * FLW-002 — "In this period but not on this filing" subsection. Rendered per
 * section (contributions / expenditures) only when that section has at least
 * one non-Included row. The rows never count toward the heading totals (which
 * equal the filing).
 */
function ExcludedRowsSubsection({
  rows,
  testIdPrefix,
  portalName,
}: {
  rows: ExcludedRow[];
  testIdPrefix: string;
  portalName: string;
}) {
  return (
    <div className="mt-4 rounded border border-muted bg-muted/30 p-3" data-testid={`${testIdPrefix}-subsection`}>
      <p className="text-sm font-medium text-muted-foreground">
        {PreflightCopy.ScheduleExcludedSubsectionHeading}
      </p>
      <ul className="mt-2 space-y-1 text-sm">
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex items-center gap-2 text-muted-foreground"
            data-testid={`${testIdPrefix}-${row.id}`}
          >
            <span>{formatDate(row.date)}</span>
            <span>{row.name}</span>
            <span>{formatAmount(row.amount)}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
              {isOnReportInclusion(row.inclusion)
                ? ''
                : excludedInclusionLabel(
                    row.inclusion,
                    row.requiresManualPortalEntry ?? false,
                    portalName,
                  )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * §32 — the "enter these in the {portal} by hand" surface for CONFIRMED in-kind
 * "Other" contributions. They're ready to file (green check) and read as "will be
 * on the filing", but the AL EDI has no field for the in-kind "Other" description,
 * so the builder omits them and Export is a silent no-op. Rather than leave the
 * treasurer at that dead-end, list each row with the exact values to type into the
 * portal (per-field copy) + a one-click "mark reported" so it leaves the pool.
 */
function ManualPortalEntryPanel({
  rows,
  reportId,
  stateDisplayName,
  portalName,
}: {
  rows: ContributionReportItem[];
  reportId: string;
  stateDisplayName: string;
  portalName: string;
}) {
  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4"
      data-testid="manual-portal-entry-panel"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-amber-900">
            {manualPortalEntryHeading(rows.length, stateDisplayName, portalName)}
          </p>
          <p className="text-sm text-amber-800">
            {manualPortalEntryBody(rows.length, stateDisplayName, portalName)}
          </p>
        </div>
      </div>
      <ul className="mt-3 space-y-3">
        {rows.map((row) => (
          <ManualPortalEntryRow key={row.id} row={row} reportId={reportId} portalName={portalName} />
        ))}
      </ul>
    </div>
  );
}

function ManualPortalEntryRow({
  row,
  reportId,
  portalName,
}: {
  row: ContributionReportItem;
  reportId: string;
  portalName: string;
}) {
  const { mutate: markReported, isPending } = useMarkContributionReported();
  const [error, setError] = useState<string | null>(null);

  const handleMarkReported = () => {
    setError(null);
    markReported(
      {
        row,
        // Distinguishes this from a triage-time mark in the audit trail: the
        // treasurer asserted they entered it directly in the portal from here.
        note: 'Hand-keyed into the filing portal from the report entry panel.',
      },
      {
        // The report detail and the previews that count this period's rows refresh from the write
        // set, and a lost race refreshes them too.
        onError: (e: unknown) => {
          setError(e instanceof Error ? e.message : 'Could not mark this as reported.');
        },
      },
    );
  };

  // The exact values the treasurer re-keys into the portal. Nature is fixed
  // ("Other" by definition); the description only shows when present.
  // Short, single-line fields sit in a 2-col grid (label left, value right). The
  // free-text description is the one field that can run long, so it gets its own
  // full-width, wrapping block below (never truncated — it's the value the copy
  // affordance exists for).
  const compactFields: { key: string; label: string; value: string }[] = [
    { key: 'contact', label: 'Contact', value: row.contactName },
    { key: 'amount', label: 'Amount', value: formatAmount(row.amount) },
    { key: 'date', label: 'Date', value: formatDate(row.date) },
    { key: 'designation', label: 'Designation', value: electionDesignationLabel(row.electionDesignation, row.electionYear) },
    { key: 'nature', label: 'Nature', value: PreflightCopy.ManualPortalEntryNatureValue },
  ];

  return (
    <li
      className="rounded-md border border-amber-200 bg-white p-3"
      data-testid={`manual-portal-row-${row.id}`}
    >
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {compactFields.map((f) => (
          <div key={f.key} className="flex items-baseline justify-between gap-2">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">{f.label}</dt>
            <dd className="flex min-w-0 items-center gap-1 text-sm font-medium text-foreground">
              <span className="truncate">{f.value}</span>
              <CopyFieldButton value={f.value} fieldKey={f.key} rowId={row.id} label={f.label} />
            </dd>
          </div>
        ))}
        {row.inKindDescription && (
          <div className="flex flex-col gap-0.5 sm:col-span-2">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">Description</dt>
            <dd className="flex items-start gap-1 text-sm font-medium text-foreground">
              <span className="break-words">{row.inKindDescription}</span>
              <CopyFieldButton
                value={row.inKindDescription}
                fieldKey="description"
                rowId={row.id}
                label="Description"
              />
            </dd>
          </div>
        )}
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button
          variant="outline"
          size="sm"
          onClick={handleMarkReported}
          disabled={isPending}
          data-testid={`manual-portal-mark-reported-${row.id}`}
        >
          <Check className="mr-1 h-4 w-4" aria-hidden="true" />
          {isPending ? 'Marking…' : PreflightCopy.ManualPortalEntryMarkReportedAction}
        </Button>
        <span className="text-xs text-muted-foreground">
          {PreflightCopy.ManualPortalEntryMarkReportedHint.replace('{portal}', portalName)}
        </span>
      </div>
      {error && (
        <p className="mt-2 text-sm text-destructive" data-testid={`manual-portal-error-${row.id}`}>
          {error}
        </p>
      )}
    </li>
  );
}

/** Per-field copy control — copies just this value to the clipboard, with a brief
 *  check-mark confirmation. Icon-only (label lives in the aria-label / title). */
function CopyFieldButton({
  value,
  fieldKey,
  rowId,
  label,
}: {
  value: string;
  fieldKey: string;
  rowId: string;
  label: string;
}) {
  const [copied, flashCopied] = useFlashFlag(1500);
  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(value);
      flashCopied();
    } catch {
      // Clipboard unavailable (insecure context) or permission denied — leave the
      // icon unchanged rather than surfacing a hard error for a convenience action.
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      title={`Copy ${label}`}
      data-testid={`manual-portal-copy-${fieldKey}-${rowId}`}
      className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-amber-100 hover:text-foreground"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}

// Shared expenditure schedule table (Date · Payee · Amount · Category · Purpose).
// Rendered once for the itemized rows and once for the non-itemized list so the two
// sections stay column-identical and a treasurer reads them the same way. Rows go
// through ExpenditureRow (edit affordance, synthetic-fee "(estimated)" marker +
// inline override), so the non-itemized set is a real inspectable list a treasurer
// can verify or itemize row by row, not a rolled-up total.
function ExpenditureScheduleTable({
  rows,
  reportId,
  onEditTransaction,
}: {
  rows: ExpenditureReportItem[];
  reportId: string;
  onEditTransaction: ((expenditureId: string) => void) | null;
}) {
  return (
    <table className="mt-2 w-full text-sm">
      <thead>
        <tr className="border-b text-left text-muted-foreground">
          <th className="pb-2 pr-4 font-medium">Date</th>
          <th className="pb-2 pr-4 font-medium">Payee</th>
          <th className="pb-2 pr-4 font-medium text-right">Amount</th>
          <th className="pb-2 pr-4 font-medium">Category</th>
          <th className="pb-2 font-medium">Purpose</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((item) => (
          <ExpenditureRow
            key={item.id}
            item={item}
            reportId={reportId}
            onEditTransaction={onEditTransaction}
          />
        ))}
      </tbody>
    </table>
  );
}

function ExpenditureRow({
  item,
  reportId,
  onEditTransaction,
}: {
  item: ExpenditureReportItem;
  reportId: string;
  // Non-null only on pre-filing (Ready/Overdue) reports; a Filed/Amended report
  // is a frozen as-filed snapshot with no editing affordance.
  onEditTransaction: ((expenditureId: string) => void) | null;
}) {
  const purpose = item.purpose;
  const canEdit = onEditTransaction !== null;
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-4">{formatDate(item.date)}</td>
      <td className="py-2 pr-4">
        <div className="flex items-center gap-1">
          <span>{item.contactName}</span>
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onEditTransaction!(item.id)}
              aria-label={`Edit this expenditure: ${item.contactName}`}
              title="Open in the ledger to fix or confirm this expenditure"
              className="h-8 w-8 shrink-0 text-primary"
            >
              <Pencil aria-hidden="true" />
            </Button>
          )}
          <ReportedRowBadge inclusion={item.inclusion} testId={`expenditure-reported-badge-${item.id}`} />
        </div>
        {item.payeeAddress && (
          <div className="text-xs text-muted-foreground">{item.payeeAddress}</div>
        )}
      </td>
      <td className="py-2 pr-4 text-right">{formatAmount(item.amount)}</td>
      <td className="py-2 pr-4">{item.category}</td>
      <td className="py-2">{purpose}</td>
    </tr>
  );
}

// #136 — the on-screen refunds table (Schedule D disbursements). Read-only: refunds are
// entered/edited from the ledger, not this preview. Column-aligned with the expenditure
// table (Date / Payee / Amount) so a treasurer reads the two disbursement lists the same way.
function RefundScheduleTable({ rows }: { rows: RefundReportItem[] }) {
  return (
    <table className="mt-2 w-full text-sm">
      <thead>
        <tr className="border-b text-left text-muted-foreground">
          <th className="pb-2 pr-4 font-medium">Date</th>
          <th className="pb-2 pr-4 font-medium">Payee</th>
          <th className="pb-2 font-medium text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((item) => (
          <tr key={item.id} className="border-b last:border-0" data-testid={`refund-row-${item.id}`}>
            <td className="py-2 pr-4">{formatDate(item.date)}</td>
            <td className="py-2 pr-4">{item.contactName}</td>
            <td className="py-2 text-right">{formatAmount(item.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ============================================================================
// Portal-only (Alabama) three-step submission flow
// ============================================================================

type SubmissionStepState = 'active' | 'completed' | 'locked' | 'skipped';

function StepBadge({ number, state }: { number: number; state: SubmissionStepState }) {
  const base =
    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold';
  if (state === 'completed') {
    return (
      <div className={cn(base, 'bg-green-600 text-white')}>
        <Check className="h-4 w-4" aria-hidden="true" />
      </div>
    );
  }
  if (state === 'active') {
    return <div className={cn(base, 'bg-primary text-primary-foreground')}>{number}</div>;
  }
  return <div className={cn(base, 'bg-muted text-muted-foreground')}>{number}</div>;
}

/**
 * One column of the horizontal stepper: the numbered badge + a connector line
 * reaching toward the next step, the step title, and the step's state-specific
 * body (active controls / completed summary / skipped or locked note).
 *
 * On wide screens the three steps sit left-to-right with connectors between the
 * badges; the grid collapses to a single stacked column on narrow screens and at
 * large font scales, and the horizontal connectors hide there (they'd be
 * meaningless once the steps stack).
 */
function SubmissionStep({
  number,
  title,
  state,
  isLast = false,
  children,
  completedSummary,
  skippedNote,
  lockedNote,
}: {
  number: number;
  title: string;
  state: SubmissionStepState;
  /** The final step draws no trailing connector. */
  isLast?: boolean;
  children?: ReactNode;
  completedSummary?: string;
  skippedNote?: string;
  lockedNote?: string;
}) {
  const muted = state === 'locked' || state === 'skipped';
  return (
    <div
      className={cn(
        'rounded-lg p-3 transition-colors',
        // Primary emphasis is reserved for the one action the treasurer can take now.
        state === 'active' && 'border border-primary/30 bg-primary/5',
      )}
      data-testid={`submission-step-${number}`}
      data-state={state}
    >
      <div className="flex items-center gap-3">
        <StepBadge number={number} state={state} />
        {!isLast && <div className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" />}
      </div>
      <p className={cn('mt-2 font-medium leading-6', muted && 'text-muted-foreground')}>{title}</p>
      <div className="mt-1 space-y-3">
        {state === 'active' && children}
        {state === 'completed' && completedSummary && (
          <p className="text-sm text-muted-foreground">{completedSummary}</p>
        )}
        {state === 'skipped' && skippedNote && (
          <p className="text-sm text-muted-foreground">{skippedNote}</p>
        )}
        {state === 'locked' && lockedNote && (
          <p className="text-sm text-muted-foreground">{lockedNote}</p>
        )}
      </div>
    </div>
  );
}

/**
 * The three-step gated filing flow for portal-only (append-only) jurisdictions
 * like Alabama. Steps are always visible so the treasurer sees the whole process;
 * only the active step is unlocked.
 *
 * File Report is intentionally absent — uploading a full package to an
 * append-only portal duplicates every previously-accepted transaction.
 */
function PortalOnlySubmissionSteps({
  report,
  stateCode,
  portalName,
  onRecorded,
}: {
  report: ComplianceReportDetailDto;
  stateCode: string | null;
  portalName: string;
  onRecorded: (message: string) => void;
}) {
  const { markExportBatchUploaded } = useMarkExportBatchUploaded();
  const preflightRead = useFilingPreflight(report.id);
  const preflight = preflightRead.status === 'ready' ? preflightRead.data : null;
  const [confirmBusy, setConfirmBusy] = useState<'accept' | 'discard' | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const liveExportDraft = report.liveExportDraft as LiveExportDraftDto | null;
  const hasPendingExport = liveExportDraft !== null;

  const step1State: SubmissionStepState = hasPendingExport
    ? 'completed'
    : report.hasUnreportedTransactions
      ? 'active'
      : 'skipped';
  const step2State: SubmissionStepState = hasPendingExport ? 'active' : 'locked';
  const step3State: SubmissionStepState =
    !hasPendingExport && !report.hasUnreportedTransactions ? 'active' : 'locked';

  const handleConfirm = async (accepted: boolean) => {
    if (!liveExportDraft) return;
    setConfirmBusy(accepted ? 'accept' : 'discard');
    setConfirmError(null);
    try {
      await markExportBatchUploaded({
        exportBatchId: liveExportDraft.exportBatchId,
        accepted,
        reportId: report.id,
      });
    } catch (e) {
      setConfirmError(e instanceof Error ? e.message : 'Failed to confirm the export upload');
    } finally {
      setConfirmBusy(null);
    }
  };

  const rowCount = liveExportDraft?.rowCount ?? 0;
  const rowLabel = rowCount === 1 ? 'transaction' : 'transactions';

  return (
    <div data-testid="portal-only-submission-steps" className="space-y-4">
      {/* The current action leads the surface at full width. Treasurers tend to scan the
          report immediately below, so the export control must be encountered before the
          compact progress row rather than squeezed into one third of it. */}
      {step1State === 'active' && (
        <div
          className="overflow-hidden rounded-lg border border-primary/30 bg-primary/5"
          data-testid="portal-export-action"
        >
          <div className="grid gap-4 p-4 sm:grid-cols-[minmax(0,1fr)_minmax(15rem,20rem)] sm:items-center">
            <div>
              <p className="text-xs font-semibold text-primary">Next step · 1 of 3</p>
              <p className="mt-1 font-semibold text-foreground">Export new transactions</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Download this period&apos;s file first. Then upload it to the {portalName} and return
                here to confirm the portal accepted it.
              </p>
            </div>
            <ExportDeltaButton report={report} stateCode={stateCode} variant="primary" />
          </div>
          <div className="px-4 pb-4 empty:hidden">
            <PreflightUnready read={preflightRead} reportId={report.id} />
            <PortalActionList warnings={preflight?.warnings ?? []} />
          </div>
        </div>
      )}

      {/* Compact progress remains visible so the full ordered workflow is legible, but only
          the current step receives blue emphasis. Future steps stay neutral and locked. */}
      <div data-testid="electronic-submission-stepper" className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Complete all three steps to submit this report to the {portalName}.
        </p>
        <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-3">
          <SubmissionStep
            number={1}
            title="Export"
            state={step1State}
            completedSummary={
              liveExportDraft
                ? `Downloaded ${rowCount} ${rowLabel} · ${formatDate(liveExportDraft.generatedAt.slice(0, 10))}`
                : undefined
            }
            skippedNote="No new transactions this period."
          />

          <SubmissionStep
            number={2}
            title="Confirm upload"
            state={step2State}
            lockedNote="Available after you export and upload to the portal."
          >
            <p className="text-sm text-muted-foreground">
              Upload the exported file to the {portalName}, then come back here to confirm.
            </p>
          </SubmissionStep>

          <SubmissionStep
            number={3}
            title="Mark filed in portal"
            state={step3State}
            isLast
            lockedNote="Available after confirming every upload."
          >
            <p className="text-sm text-muted-foreground">
              This finalizes the report and closes the period — the transactions lock from further
              edits. Do this once you&apos;ve uploaded and confirmed every transaction for this
              period in the {portalName}. You can reopen the period later if something needs to
              change.
            </p>
            <MarkFiledInPortalButton report={report} stateCode={stateCode} onRecorded={onRecorded} />
          </SubmissionStep>
        </div>
      </div>

      {/* Step 2's confirm body lives below the stepper row so the accept/discard
          decision + the append-only "duplicates every transaction" warning aren't
          crammed into a one-third-width column. Only shown while step 2 is active
          (a pending export exists), when steps 1 and 3 are compact. */}
      {step2State === 'active' && (
        <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
          <div className="space-y-2 text-sm">
            <div className="space-y-2 pl-2">
              <div className="flex gap-2">
                <span className="mt-0.5 shrink-0 select-none text-foreground">·</span>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">If the portal accepted it</span> —
                  confirm below. Those transactions are permanently marked as uploaded and won&apos;t
                  appear in future exports.
                </p>
              </div>
              <div className="flex gap-2">
                <span className="mt-0.5 shrink-0 select-none text-foreground">·</span>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">
                    If the portal rejected it, or you need to make changes
                  </span>{' '}
                  — choose &quot;No, try again&quot;. The export is discarded so you can adjust and
                  re-export. Do not confirm an upload the portal rejected — those transactions would
                  be excluded from all future exports and never filed.
                </p>
              </div>
            </div>
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              Re-uploading a file the portal already accepted will duplicate every transaction. Only
              upload each export once.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => handleConfirm(true)}
              disabled={confirmBusy !== null}
              data-testid="export-gate-accept"
            >
              {confirmBusy === 'accept' ? 'Confirming…' : 'Yes, portal accepted it'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleConfirm(false)}
              disabled={confirmBusy !== null}
              data-testid="export-gate-discard"
            >
              {confirmBusy === 'discard' ? 'Discarding…' : 'No, try again'}
            </Button>
          </div>
          {confirmError && (
            <p className="text-sm text-destructive" data-testid="export-gate-error">
              {confirmError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * #132 / #482 — the submission surface for a whole-report portal-upload jurisdiction (generate ≠
 * file, ADR-019). Every jurisdiction here is `uploadIsFiling = false`: the treasurer downloads the
 * report, gets it into the state's system themselves, then records the filing via "Mark filed in
 * portal" (which also captures the portal's / FEC's receipt number into the State Filing Receipt
 * shown once Filed). The generate CONTROL depends on `certifyAtGenerate`:
 *   • VA/NC (certifyAtGenerate = false) — the payload is unsigned data the treasurer certifies
 *     inside the portal, so a plain, non-attesting `GenerateWholeReportButton` (`POST /generate`).
 *   • federal (certifyAtGenerate = true) — the `.fec` Form 3 cover embeds the treasurer's signed
 *     certification, so generating it REQUIRES the attestation review: `FilePackageButton`
 *     (`POST /file`), which now produces the attested `.fec` WITHOUT filing it (the report stays
 *     Ready, freely regeneratable). The treasurer uploads it to efo.fec.gov, then marks it filed.
 * The boundary copy states plainly that nothing is filed automatically. Portal/state names are
 * composed by the caller (never hard-coded "Virginia" / "COMET portal").
 */
function WholeReportPortalUploadSteps({
  report,
  filingDestination,
  isEmailFiling,
  certifyAtGenerate,
  officeName,
  stateName,
  captureStateReceipt,
  separateSignedCover,
  onRecorded,
}: {
  onRecorded: (message: string) => void;
  report: ComplianceReportDetailDto;
  filingDestination: string;
  // The served receiving-office name (`profile.portalName`) and state display name, so the email
  // steps name THIS jurisdiction's office (NC → NCSBE; MS → the Secretary of State / Circuit Clerk).
  officeName: string;
  stateName: string;
  // proofOfFiling (MS) — the confirm dialog offers the office's receipt as an optional atomic pair.
  captureStateReceipt: boolean;
  // The declared separateSignedCover axis (NC's CRO-1000) — the generate step offers the cover.
  separateSignedCover: boolean;
  // #313 — NC files by EMAIL (the .cfd goes to the NCSBE inbox), not a portal upload. When true the
  // pre-generate note + mark-filed hint are worded around email; the button surfaces the CRO-1000
  // cover and NC email success copy.
  isEmailFiling: boolean;
  // #482 — federal: its generate step is the attestation File flow (the .fec embeds certification).
  certifyAtGenerate: boolean;
}) {
  return (
    <div data-testid="whole-report-portal-upload" className="space-y-4">
      <p className="text-sm text-muted-foreground" data-testid="whole-report-portal-upload-note">
        {isEmailFiling ? (
          withReceivingOffice(PreflightCopy.GenerateEmailStep, officeName).replace('{state}', stateName)
        ) : certifyAtGenerate ? (
          <>
            Generate and download your report here — you'll confirm your treasurer certification as
            part of generating it. Nothing is filed automatically: upload the downloaded <code>.fec</code>{' '}
            file to the FEC's e-filing system (efo.fec.gov), then come back and mark it filed with the
            FEC filing id. You can regenerate a fresh file freely until you do.
          </>
        ) : (
          <>
            Generate this report, then upload it to {filingDestination}. Nothing is filed
            automatically — after you upload and submit it in the portal, come back and mark it
            filed here.
          </>
        )}
      </p>
      {certifyAtGenerate ? (
        <FilePackageButton
          report={report}
          stateCode={report.stateCode}
          variant="primary"
          nonFinalizing
        />
      ) : (
        <GenerateWholeReportButton
          reportId={report.id}
          variant="primary"
          isEmailFiling={isEmailFiling}
          officeName={officeName}
          separateSignedCover={separateSignedCover}
        />
      )}
      {/* Issue #861 — an email-filing report (NC, MS) can be sent to its receiving office on the
          campaign's behalf, between generating it and confirming it filed. The report stays Ready;
          "sent" is a derived lens, and the modal always offers the download-and-file-it-yourself
          escape hatch. It lives here because this is the path email filers actually take. */}
      {isEmailFiling && <AuthorizeSendButton reportId={report.id} />}
      {/* ADR-023 — the committing finalizer. Hidden once the report is Filed via
          PortalAttested (canReopen), when Reopen is the only forward move (rendered above). */}
      {!report.canReopen && (
        <div className="space-y-1">
          <MarkFiledInPortalButton
            report={report}
            stateCode={report.stateCode}
            certifyAtGenerate={certifyAtGenerate}
            captureStateReceipt={captureStateReceipt}
            onRecorded={onRecorded}
          />
          <p className="text-sm text-muted-foreground">
            {isEmailFiling
              ? `Record that you already emailed your report file to ${receivingOfficePhrase(officeName)}.`
              : certifyAtGenerate
                ? 'Record that you already uploaded the .fec to the FEC (efo.fec.gov) — enter the FEC filing id it gave you.'
                : `Record that you already uploaded and submitted it in ${filingDestination}.`}
          </p>
        </div>
      )}
    </div>
  );
}

// Shared contribution schedule table (Date · Contact · Amount · Type · Designation
// · Agg-to-Date). Rendered once for the itemized rows and once for the non-itemized
// list so the two sections stay column-identical and a treasurer reads them the same
// way. Rows go through ContributionRow (edit affordance, missing-info + manual-portal
// badges), so the non-itemized set is a real inspectable list, not a rolled-up total.
function ContributionScheduleTable({
  rows,
  portalName,
  requiresEmployer,
  onFixMissingInfo,
  onEditTransaction,
}: {
  rows: ContributionReportItem[];
  portalName: string;
  requiresEmployer: boolean;
  onFixMissingInfo: (contactId: string) => void;
  onEditTransaction: ((contributionId: string) => void) | null;
}) {
  return (
    <table className="mt-2 w-full text-sm">
      <thead>
        <tr className="border-b text-left text-muted-foreground">
          <th className="pb-2 pr-4 font-medium">Date</th>
          <th className="pb-2 pr-4 font-medium">Contact</th>
          <th className="pb-2 pr-4 font-medium text-right">Amount</th>
          <th className="pb-2 pr-4 font-medium">Type</th>
          <th className="pb-2 pr-4 font-medium">Designation</th>
          <th className="pb-2 font-medium text-right">Agg-to-Date</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((item) => (
          <ContributionRow
            key={item.id}
            item={item}
            portalName={portalName}
            requiresEmployer={requiresEmployer}
            onFixMissingInfo={onFixMissingInfo}
            onEditTransaction={onEditTransaction}
          />
        ))}
      </tbody>
    </table>
  );
}

function ContributionRow({
  item,
  portalName,
  requiresEmployer,
  onFixMissingInfo,
  onEditTransaction,
}: {
  item: ContributionReportItem;
  // §32 — resolved per jurisdiction; substituted into the "Add manually in
  // {portal}" badge an in-kind "Other" row carries (it can't ride the EDI upload).
  portalName: string;
  requiresEmployer: boolean;
  onFixMissingInfo: (contactId: string) => void;
  // Non-null only on pre-filing (Ready/Overdue) reports — a Filed/Amended
  // report is frozen, so no click-through-to-edit.
  onEditTransaction: ((contributionId: string) => void) | null;
}) {
  // The per-row missing-employer/occupation badge. `missingEmployerInfo` is server-produced
  // through the shared donor-info verdict (the itemized, on-filing twin of the report-level
  // missing-donor-info banner) — already narrowed to itemized individual subjects with no
  // accepted best-efforts, so this is a plain typed read.
  const hasMissingInfo = requiresEmployer && item.missingEmployerInfo;
  const canEdit = onEditTransaction !== null;
  // §32 — a Confirmed (ready-to-file) row the EDI omits looks like any other
  // Included row; badge it so the treasurer knows it needs portal hand-entry (the
  // panel above carries the values + the mark-reported action). Reported rows have
  // been keyed already, so the badge drops once the status flips.
  const needsManualPortal = item.requiresManualPortalEntry && item.ledgerStatus === 'Confirmed';

  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-4">{formatDate(item.date)}</td>
      <td className="py-2 pr-4">
        <div className="flex items-center gap-1">
          <span>{item.contactName}</span>
          {canEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onEditTransaction!(item.id)}
              aria-label={`Edit this contribution: ${item.contactName}`}
              title="Open in the ledger to fix or confirm this contribution"
              className="h-8 w-8 shrink-0 text-primary"
            >
              <Pencil aria-hidden="true" />
            </Button>
          )}
          {hasMissingInfo && (
            <button
              type="button"
              data-testid="missing-info-warning"
              aria-label={`Fix missing employer/occupation for ${item.contactName}`}
              title="Missing employer/occupation — click to fix"
              onClick={() => onFixMissingInfo(item.contactId)}
              className="rounded p-0.5 hover:bg-amber-100"
            >
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            </button>
          )}
          {needsManualPortal && (
            <span
              className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
              data-testid={`contribution-manual-portal-badge-${item.id}`}
            >
              {PreflightCopy.ScheduleAddManuallyInPortalLabel.replace('{portal}', portalName)}
            </span>
          )}
          <ReportedRowBadge inclusion={item.inclusion} testId={`contribution-reported-badge-${item.id}`} />
        </div>
      </td>
      <td className="py-2 pr-4 text-right">{formatAmount(item.amount)}</td>
      <td className="py-2 pr-4">{item.contributionType}</td>
      <td className="py-2 pr-4">{electionDesignationLabel(item.electionDesignation, item.electionYear)}</td>
      <td className="py-2 text-right">{formatAmount(item.aggregateToDate)}</td>
    </tr>
  );
}
